import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm, Field } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import createEffect from 'modules/core/hooks/createEffect'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import type { LegalEntities } from 'modules/core/store/collections/legalEntities'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import useDownloadFile from 'modules/employees/hooks/useDownloadFile'
import { legalEntityEmployees } from 'modules/employees/store/collections/employeeGroupEmployees'
import { type Employees } from 'modules/employees/store/collections/employees'

class LegalEntityForm extends FactorialForm {
  constructor(values: Record<string, unknown>) {
    super(values, {
      legal_entity: 'string',
    })
  }
}
interface Props {
  options: SelectOption[]
  buttonText: string
  downloadPath: string
  checkDownload?: string
  allowEmptyDownload?: boolean
  legalEntities?: LegalEntities
  employees: Employees
}

const checkDownloadability = createEffect(
  (
    legalEntity: LegalEntity,
    setDownloadable: (downloadable: boolean) => void,
    legalEntitySelect: Field,
    employees: Employees
  ) => {
    if (!legalEntity) return

    if (legalEntityEmployees(legalEntity, employees).length === 0) {
      setDownloadable(false)
      legalEntitySelect.setErrors([i18n.t('import_contract.legal_entity_with_no_employees')])
    } else {
      setDownloadable(true)
      legalEntitySelect.setErrors([])
    }
  }
)

const LegalEntitySelect = ({
  options,
  buttonText,
  downloadPath,
  checkDownload,
  allowEmptyDownload = true,
  legalEntities,
  employees,
}: Props) => {
  const api = React.useRef(ApiClient()).current
  const legalEntityForm = React.useRef(new LegalEntityForm({})).current
  const legalEntitySelect = legalEntityForm.get('legal_entity')
  const [legalEntityId, setLegalEntityId] = React.useState(options[0]?.value)
  const [downloadable, setDownloadable] = React.useState(true)

  if (!allowEmptyDownload && legalEntities) {
    checkDownloadability(
      legalEntities.find({ id: parseInt(legalEntityId) }),
      setDownloadable,
      legalEntitySelect,
      employees
    )
  }

  const { running: downloading, onSubmit: onDownloadRequest } = useDownloadFile({
    api: api,
    legalEntityId: legalEntityId,
    downloadPath: downloadPath,
    checkDownload: checkDownload,
  })

  return (
    <>
      <Select
        label={i18n.t('employees.legal_entity')}
        options={options}
        onChange={() => setLegalEntityId(legalEntityForm.get('legal_entity').value)}
        field={legalEntityForm.get('legal_entity')}
      />
      <Button onClick={onDownloadRequest} disabled={downloading || !downloadable} type="secondary">
        {buttonText}
      </Button>
    </>
  )
}

export default observer(LegalEntitySelect)
